import { useEffect, useRef } from 'react';

interface EscapeHandler {
  onEscape: () => void;
  shouldHandle?: (event: KeyboardEvent) => boolean;
}

const activeHandlers = new Set<React.RefObject<EscapeHandler>>();

function handleEscape(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  // Portaled layers are appended in opening order. Let the top Base UI layer
  // handle Escape, including nonmodal popups opened inside legacy dialogs.
  const layers = document.querySelectorAll<HTMLElement>([
    '[aria-modal="true"]',
    '[data-slot="select-content"][data-open]',
    '[data-slot="combobox-content"][data-open]',
    '[data-slot="dropdown-menu-content"][data-open]',
    '[data-slot="dropdown-menu-sub-content"][data-open]',
    '[data-slot="popover-content"][data-open]',
  ].join(', '));
  const topLayer = Array.from(layers).at(-1);
  if (topLayer?.matches('[data-slot="dialog-content"], [data-slot="alert-dialog-content"], [data-slot="sheet-content"], [data-open]')) {
    return;
  }
  const top = Array.from(activeHandlers).at(-1);
  if (!top) return;
  if (top.current.shouldHandle && !top.current.shouldHandle(event)) return;
  event.preventDefault();
  event.stopPropagation();
  top.current.onEscape();
}

/** The most recently mounted active layer handles or yields Escape. */
export function useEscapeKey(
  onEscape: () => void,
  active = true,
  shouldHandle?: (event: KeyboardEvent) => boolean,
): void {
  const callback = useRef<EscapeHandler>({ onEscape, shouldHandle });
  callback.current = { onEscape, shouldHandle };

  useEffect(() => {
    if (!active) return;
    // Base UI Sheet stops Escape during bubbling, so legacy layers claim it in capture.
    if (activeHandlers.size === 0) document.addEventListener('keydown', handleEscape, true);
    activeHandlers.add(callback);
    return () => {
      activeHandlers.delete(callback);
      if (activeHandlers.size === 0) document.removeEventListener('keydown', handleEscape, true);
    };
  }, [active]);
}
